import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { api, qs } from "@/lib/api";
import { Card, SectionTitle } from "@/components/Kpi";
import { AzzeraFiltri, FilterBar, inputCls, useFiltersMeta } from "@/components/Filters";
import { eurShort, num, troncaNome } from "@/lib/format";

const MESI_BREVI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

const TABELLE = [
  { key: "pratiche_caricate", label: "Pezzi caricati", kind: "num", colore: "red" },
  { key: "importo_caricato", label: "Importi caricati", kind: "eur", colore: "red" },
  { key: "pratiche_liquidate", label: "Pezzi liquidati", kind: "num", colore: "green" },
  { key: "importo_liquidato", label: "Importi liquidati", kind: "eur", colore: "green" },
];

const STILI = {
  red: { card: "!border-2 bd-caricato", head: "bg-caricato", val: "txt-caricato" },
  green: { card: "!border-2 bd-liquidato", head: "bg-liquidato", val: "txt-liquidato" },
};

function Pivot({ conf, rows, mesi, aperta, onToggle }) {
  const s = STILI[conf.colore];
  const fmt = (v) => (v ? (conf.kind === "eur" ? eurShort(v) : num(v)) : "—");
  const cols = Array.from({ length: mesi }, (_, i) => i);
  const dati = rows
    .map((r) => ({ ...r, valori: r[conf.key], totale: r[conf.key].reduce((t, v) => t + v, 0) }))
    .filter((r) => r.totale > 0)
    .sort((a, b) => b.totale - a.totale);
  const totMese = cols.map((i) => dati.reduce((t, r) => t + (r.valori[i] || 0), 0));
  const totale = dati.reduce((t, r) => t + r.totale, 0);

  return (
    <Card className={`p-0 overflow-hidden ${s.card}`} data-testid={`pivot-${conf.key}`}>
      <button type="button" onClick={onToggle} data-testid={`pivot-toggle-${conf.key}`}
        className={`w-full flex items-center gap-2 px-6 py-3 text-left ${s.head} text-white
          hover:brightness-110 transition-all duration-200`}>
        <ChevronDown size={16} className={`transition-transform duration-200 ${aperta ? "" : "-rotate-90"}`} />
        <span className="uppercase text-xs tracking-[0.18em] font-semibold">{conf.label}</span>
        <span className="ml-auto font-mono text-xs opacity-90">
          {num(dati.length)} convenzionati · totale {fmt(totale)}
        </span>
      </button>
      {aperta && (
        <div className="overflow-auto max-h-[620px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="text-left px-4 py-2 sticky left-0 bg-slate-50 min-w-[220px]">Ragione sociale</th>
                {cols.map((i) => (
                  <th key={i} className="text-right px-3 py-2 whitespace-nowrap">
                    {String(i + 1).padStart(2, "0")} · {MESI_BREVI[i]}
                  </th>
                ))}
                <th className="text-right px-4 py-2 whitespace-nowrap bg-slate-100">Totale complessivo</th>
              </tr>
            </thead>
            <tbody>
              {dati.map((r, idx) => (
                <tr key={r.codice_dealer}
                  className={`border-t border-slate-100 hover:bg-sky-50/60 transition-colors duration-150
                    ${idx % 2 ? "bg-slate-100/80" : "bg-white"}`}>
                  <td className={`px-4 py-2 sticky left-0 font-bold whitespace-nowrap
                    ${idx % 2 ? "bg-slate-100/80" : "bg-white"}`}>
                    <Link to={`/convenzionati/${r.codice_dealer}`} className="hover:underline"
                      title={r.ragione_sociale}>
                      {troncaNome(r.ragione_sociale)}
                    </Link>
                  </td>
                  {cols.map((i) => (
                    <td key={i} className="px-3 py-2 text-right font-mono whitespace-nowrap text-slate-600">
                      {fmt(r.valori[i])}
                    </td>
                  ))}
                  <td className={`px-4 py-2 text-right font-mono font-semibold whitespace-nowrap bg-slate-50 ${s.val}`}>
                    {fmt(r.totale)}
                  </td>
                </tr>
              ))}
              {!dati.length && (
                <tr><td colSpan={mesi + 2} className="px-4 py-6 text-slate-400">
                  Nessun dato per i filtri selezionati.
                </td></tr>
              )}
              <tr className="border-t-2 border-slate-300 bg-slate-100 font-semibold">
                <td className="px-4 py-3 sticky left-0 bg-slate-100">Totale complessivo</td>
                {totMese.map((v, i) => (
                  <td key={i} className="px-3 py-3 text-right font-mono whitespace-nowrap">{fmt(v)}</td>
                ))}
                <td className="px-4 py-3 text-right font-mono whitespace-nowrap">{fmt(totale)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** Classifiche dealer con dettaglio mensile (pezzi e importi, caricato e liquidato). */
export default function ClassificheDealerMensili() {
  const { slug } = useParams();
  const chiave = `classifiche-dealer:${slug || "totale"}`;
  const salvato = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(chiave)) || {}; } catch { return {}; }
  }, [chiave]);
  const meta = useFiltersMeta();
  const [filters, setFilters] = useState(
    salvato.filters || { anno: 2026, ...(slug ? { commerciale: slug } : {}) });
  const [dealers, setDealers] = useState([]);
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [chiuse, setChiuse] = useState(salvato.chiuse || {});
  const [reset, setReset] = useState(0);

  useEffect(() => {
    let s = {};
    try { s = JSON.parse(localStorage.getItem(chiave)) || {}; } catch { s = {}; }
    setFilters(s.filters || { anno: 2026, ...(slug ? { commerciale: slug } : { commerciale: "" }) });
    setChiuse(s.chiuse || {});
    setSearch("");
  }, [chiave, slug]);

  useEffect(() => {
    localStorage.setItem(chiave, JSON.stringify({ filters, chiuse }));
  }, [chiave, filters, chiuse]);

  useEffect(() => {
    api.get(`/dealers-classifiche-mensili?${qs(filters)}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [filters]);

  useEffect(() => {
    api.get(`/dashboard/dealers-periodo?${qs({ anno: filters.anno, commerciale: filters.commerciale })}`)
      .then((r) => setDealers(r.data.dealers || [])).catch(() => setDealers([]));
  }, [filters.anno, filters.commerciale]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = data?.rows || [];
    return q ? all.filter((r) => (r.ragione_sociale || "").toLowerCase().includes(q)) : all;
  }, [data, search]);

  return (
    <div data-testid="classifiche-dealer-mensili-page">
      <SectionTitle sub={data
        ? `Dettaglio mensile ${data.anno} · ${num(rows.length)} convenzionati · gennaio–${MESI_BREVI[data.mesi - 1]}`
        : "Caricamento…"}>
        Classifiche Dealer
      </SectionTitle>

      <div className="flex flex-wrap items-center gap-3">
        <FilterBar key={reset} meta={meta} value={filters} onChange={setFilters} dealers={dealers}
          fields={slug ? ["anno", "settore", "dealer"] : ["anno", "commerciale", "settore", "dealer"]} />
        <input className={`${inputCls} min-w-[260px] mb-6`} placeholder="Cerca ragione sociale…"
          value={search} onChange={(e) => setSearch(e.target.value)} data-testid="classifiche-mensili-search" />
        <AzzeraFiltri onClick={() => {
          setFilters({ anno: 2026, ...(slug ? { commerciale: slug } : { commerciale: "" }) });
          setSearch("");
          setReset((v) => v + 1);
        }} />
      </div>

      <div className="space-y-6">
        {TABELLE.map((c) => (
          <Pivot key={c.key} conf={c} rows={rows} mesi={data?.mesi || 12}
            aperta={!chiuse[c.key]}
            onToggle={() => setChiuse((v) => ({ ...v, [c.key]: !v[c.key] }))} />
        ))}
      </div>
    </div>
  );
}
